import { useMemo } from "react";
import { useListContext } from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SortButton } from "@/components/admin/sort-button";
import { SearchInput } from "@/components/admin/search-input";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router";
import { ExternalLink } from "lucide-react";

import { supabase } from "../providers/supabase/supabase";
import { TopToolbar } from "../layout/TopToolbar";
import type { Entity } from "../types";

type EntitySummary = Entity & { company_name?: string };

const entityFilters = [
  <SearchInput source="q" alwaysOn />,
];

export const EntityList = () => {
  return (
    <List
      resource="entities_summary"
      title={false}
      perPage={25}
      sort={{ field: "name", order: "ASC" }}
      actions={<EntityListActions />}
      pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
      filters={entityFilters}
    >
      <EntityListContent />
    </List>
  );
};

const EntityListActions = () => (
  <TopToolbar>
    <SortButton
      fields={["name", "state", "planted_area_ha", "ffb_per_ha"]}
    />
  </TopToolbar>
);

const EntityListContent = () => {
  const { data: entities, isPending, error } = useListContext<EntitySummary>();

  // Fetch entity IDs that are in active account deals
  const { data: activeDeals } = useQuery({
    queryKey: ["active_deal_entity_ids"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select("entity_ids")
        .eq("type", "account")
        .is("archived_at", null);
      if (error) throw error;
      return data as { entity_ids: number[] | null }[];
    },
    staleTime: 30_000,
  });

  const inDealSet = useMemo(() => {
    const ids = new Set<number>();
    for (const d of activeDeals ?? []) {
      for (const id of d.entity_ids ?? []) ids.add(id);
    }
    return ids;
  }, [activeDeals]);

  if (isPending || error) return null;
  if (!entities?.length) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        No entities found. Entity data is populated by the scraper.
      </div>
    );
  }

  return (
    <div className="divide-y">
      <div className="grid grid-cols-6 gap-4 px-4 py-2 text-sm font-medium text-muted-foreground">
        <span className="col-span-2">Name</span>
        <span>State</span>
        <span className="text-right">Planted (Ha)</span>
        <span className="text-right">FFB/Ha</span>
        <span className="text-center">PDF</span>
      </div>
      {entities.map((entity) => (
        <Link
          key={entity.id}
          to={`/entities/${entity.id}/show`}
          className="grid grid-cols-6 gap-4 px-4 py-3 text-sm hover:bg-muted transition-colors items-center"
        >
          <div className="col-span-2">
            <div className="flex items-center gap-2">
              <span className="font-medium">{entity.name}</span>
              {inDealSet.has(Number(entity.id)) && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  In Deal
                </Badge>
              )}
            </div>
            {entity.company_name && (
              <div className="text-xs text-muted-foreground">
                {entity.company_name}
              </div>
            )}
          </div>
          <span>{entity.state || "—"}</span>
          <span className="text-right">
            {entity.planted_area_ha?.toLocaleString() || "—"}
          </span>
          <span className="text-right">
            {entity.ffb_per_ha?.toLocaleString() || "—"}
          </span>
          <span className="text-center">
            {entity.pdf_drive_link ? (
              <a
                href={entity.pdf_drive_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="h-4 w-4 inline" />
              </a>
            ) : "—"}
          </span>
        </Link>
      ))}
    </div>
  );
};
